/**
 * ThemeProvider — Premium Fintech Design System
 * 
 * Provides light/dark mode theming via CSS custom properties (web).
 * On native, provides the context with light mode default (no CSS vars).
 * Persists user preference to localStorage (web only).
 * Respects system prefers-color-scheme as default (web only).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { updateMobileTheme } from './darkColors';
import { applyMobileTheme } from './styleSheetInterceptor';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggle: () => {},
  setMode: () => {},
  isDark: false,
});

export const useTheme = () => useContext(ThemeContext);

/* ─── Color Tokens ──────────────────────────────────────────── */
const LIGHT_TOKENS: Record<string, string> = {
  '--sc-bg-primary': '#F5F7FA',
  '--sc-bg-secondary': '#FFFFFF',
  '--sc-bg-elevated': '#FFFFFF',
  '--sc-bg-hero': '#0A1C30',
  '--sc-bg-hero-gradient': 'linear-gradient(135deg, #0A1C30 0%, #142D4C 60%, #1A3A5C 100%)',
  '--sc-bg-card': '#FFFFFF',
  '--sc-bg-input': '#F8FAFC',
  '--sc-bg-hover': '#F1F5F9',
  '--sc-bg-active': '#E8F1FD',
  '--sc-bg-skeleton': '#E2E8F0',
  '--sc-bg-overlay': 'rgba(0, 0, 0, 0.5)',
  '--sc-bg-sheet': '#FFFFFF',
  '--sc-bg-tab-bar': '#FFFFFF',
  '--sc-brand-primary': '#0C5ADB',
  '--sc-brand-primary-light': '#4E8DF5',
  '--sc-brand-primary-dark': '#0840A0',
  '--sc-brand-primary-bg': '#E8F1FD',
  '--sc-brand-gradient': 'linear-gradient(135deg, #0C5ADB 0%, #0840A0 100%)',
  '--sc-brand-gradient-light': 'linear-gradient(135deg, #4E8DF5 0%, #0C5ADB 100%)',
  '--sc-logo-filter': 'none',
  '--sc-text-primary': '#0A1C30',
  '--sc-text-secondary': '#475569',
  '--sc-text-tertiary': '#94A3B8',
  '--sc-text-inverse': '#FFFFFF',
  '--sc-text-on-brand': '#FFFFFF',
  '--sc-text-link': '#0C5ADB',
  '--sc-border': '#E2E8F0',
  '--sc-border-light': '#F1F5F9',
  '--sc-divider': '#F1F5F9',
  '--sc-success': '#229A39',
  '--sc-success-bg': '#E6F4EA',
  '--sc-success-text': '#0B6623',
  '--sc-warning': '#F59E0B',
  '--sc-warning-bg': '#FEF3C7',
  '--sc-warning-text': '#92400E',
  '--sc-error': '#EF4444',
  '--sc-error-bg': '#FEE2E2',
  '--sc-error-text': '#B91C1C',
  '--sc-info': '#3B82F6',
  '--sc-info-bg': '#DBEAFE',
  '--sc-info-text': '#1E40AF',
  '--sc-shadow-xs': '0 1px 2px rgba(0, 0, 0, 0.04)',
  '--sc-shadow-sm': '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
  '--sc-shadow-md': '0 4px 12px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)',
  '--sc-shadow-lg': '0 10px 30px -8px rgba(15, 23, 42, 0.08), 0 4px 8px rgba(0, 0, 0, 0.03)',
  '--sc-shadow-xl': '0 20px 50px -12px rgba(15, 23, 42, 0.12), 0 8px 16px rgba(0, 0, 0, 0.04)',
  '--sc-shadow-brand': '0 4px 14px rgba(12, 90, 219, 0.25), 0 1px 3px rgba(0, 0, 0, 0.05)',
  '--sc-shadow-card': '0 1px 3px rgba(0, 0, 0, 0.02), 0 8px 24px -6px rgba(15, 23, 42, 0.05)',
  '--sc-shadow-tab-bar': '0 -4px 16px rgba(15, 23, 42, 0.04)',
  '--sc-radius-xs': '4px',
  '--sc-radius-sm': '8px',
  '--sc-radius-md': '12px',
  '--sc-radius-lg': '16px',
  '--sc-radius-xl': '20px',
  '--sc-radius-2xl': '24px',
  '--sc-radius-full': '9999px',
  '--sc-space-1': '4px',
  '--sc-space-2': '8px',
  '--sc-space-3': '12px',
  '--sc-space-4': '16px',
  '--sc-space-5': '20px',
  '--sc-space-6': '24px',
  '--sc-space-8': '32px',
  '--sc-space-10': '40px',
  '--sc-space-12': '48px',
  '--sc-space-16': '64px',
  '--sc-font-family': "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  '--sc-font-display': "'Outfit', 'Inter', -apple-system, sans-serif",
  '--sc-font-size-xs': '11px',
  '--sc-font-size-sm': '13px',
  '--sc-font-size-base': '14px',
  '--sc-font-size-md': '15px',
  '--sc-font-size-lg': '17px',
  '--sc-font-size-xl': '20px',
  '--sc-font-size-2xl': '24px',
  '--sc-font-size-3xl': '28px',
  '--sc-font-size-4xl': '34px',
  '--sc-transition-fast': '0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  '--sc-transition-base': '0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  '--sc-transition-slow': '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '--sc-transition-spring': '0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
  '--sc-touch-min': '44px',
  '--sc-touch-btn': '48px',
  '--sc-touch-input': '48px',
  '--sc-tab-bar-height': '60px',
  '--sc-header-height': '56px',
};

const DARK_TOKENS: Record<string, string> = {
  ...LIGHT_TOKENS,
};

/* ─── Provider Component ────────────────────────────────────── */
const STORAGE_KEY = 'sc-theme-mode';

function getInitialMode(): ThemeMode {
  return 'light';
}

function applyTokens(mode: ThemeMode) {
  if (Platform.OS !== 'web') return;
  const tokens = LIGHT_TOKENS;
  const root = document.documentElement;
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  root.setAttribute('data-theme', 'light');
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>('light');

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState('light');
    if (Platform.OS === 'web') {
      try { localStorage.setItem(STORAGE_KEY, 'light'); } catch {}
      applyTokens('light');
    } else {
      try { AsyncStorage.setItem(STORAGE_KEY, 'light'); } catch {}
      updateMobileTheme('light');
      applyMobileTheme('light');
    }
  }, []);

  const toggle = useCallback(() => {
    setMode('light');
  }, [setMode]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      applyTokens('light');
    } else {
      updateMobileTheme('light');
      applyMobileTheme('light');
    }
  }, []);

  useEffect(() => {
    const loadPersistedTheme = async () => {
      try {
        setMode('light');
      } catch (err) {
        console.log('Error loading theme:', err);
      }
    };
    loadPersistedTheme();
  }, [setMode]);

  return (
    <ThemeContext.Provider value={{ mode: 'light', toggle, setMode, isDark: false }}>
      {children}
    </ThemeContext.Provider>
  );
};
